import React, { useEffect, useRef, useState } from "react";
import { styled, useIsMediumScreenSize } from "../../theme/styled";
import { zIndex } from "../../theme/zIndex";
import { GreenLogo } from "../logo/GreenLogo";
import type { LogoTextPositions } from "../logo/GreenLogo";
import { Flex } from "../util/Flex";

interface TopBarProps {
  children?: React.ReactNode | React.ReactNode[];
  dataTestId?: string;
  alignLogo?: LogoAlignment;
  logoText?: string;
  logoAriaLabel?: string;
  logoTextPosition?: LogoTextPositions;
  logoHref: string;
  hideLogo?: boolean;
  onClickLogo?: (
    e: React.KeyboardEvent<Element> | React.MouseEvent<Element, MouseEvent>,
  ) => unknown;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  /* If true the TopBar will be transparent initally until it scrolls past a certain height */
  transparentUntilScroll?: boolean;
}

type LogoAlignment = "left" | "center" | "right";

const AppBar = styled.header<{
  transparent?: boolean;
  animation?: boolean;
}>((p) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "$responsive-topbar-height",
  transition: p.animation ? "all 250ms ease 0s" : undefined,
  boxShadow: p.transparent ? "none" : "0px 2px 8px 0px $black6%",
  background: p.transparent ? "transparent" : "$background-secondary",
}));

const AppBarContent = styled.div({
  position: "relative",
  padding: ["$spacing-0", "$spacing-24"],

  $medium: {
    padding: ["$spacing-0", "$spacing-16"],
  },
});

/* This is to make sure the rest of the content stays below the <TopBar /> and not hidden behind it */
const AppBarSticky = styled.div({
  position: "sticky",
  top: 0,
  height: "$responsive-topbar-height",
  zIndex: zIndex.appBar,
});

const LogoContainer = styled(Flex)<Pick<TopBarProps, "alignLogo">>((p) => ({
  position: "absolute",
  width: "auto",
  height: "$responsive-topbar-height",
  left: p.alignLogo === "center" ? "50%" : "unset",
  right: p.alignLogo === "right" ? "0" : "unset",
  transform: p.alignLogo === "center" ? "translateX(-50%)" : "unset",
}));

const InnerContent = styled.div({
  color: "!!$gray500",
  display: "flex",
  flex: "1 1 100%",
  alignItems: "center",
});

const Adornment = styled(Flex)({
  width: "100%",
  height: "$responsive-topbar-height",
});

const EndAdornment = styled(Flex)({
  width: "100%",
});

const ObserverPixel = styled.div({
  position: "absolute",
  width: "1px",
  height: "1px",
  top: "20px",
  left: 0,
});

export const TopBar = (props: TopBarProps): React.JSX.Element => {
  const alignLogo: TopBarProps["alignLogo"] = props.alignLogo ?? "center";
  const showLogo = !props.hideLogo;
  const isMediumScreenSize = useIsMediumScreenSize();
  const [isTransparent, setIsTransparent] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.transparentUntilScroll) {
      return;
    }

    const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
      const [entry] = entries;

      if (entry) {
        setIsTransparent(entry.isIntersecting);
      }
    };

    const isIntersectionObserverSupported = "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window &&
      "intersectionRatio" in window.IntersectionObserverEntry.prototype;

    if (isIntersectionObserverSupported) {
      const observer = new IntersectionObserver(handleIntersection);

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    } else {
      return setIsTransparent(false);
    }
  }, [isMediumScreenSize, props.transparentUntilScroll]);

  return (
    <>
      {props.transparentUntilScroll && <ObserverPixel ref={ref} aria-hidden />}
      <AppBarSticky>
        <AppBar
          role="banner"
          transparent={props.transparentUntilScroll && isTransparent}
          animation={props.transparentUntilScroll}
          data-test-id={props.dataTestId}
          data-component-type="TopBar"
        >
          <AppBarContent>
            {showLogo && (
              <LogoContainer alignLogo={alignLogo} alignItems="center">
                <GreenLogo
                  size={isMediumScreenSize ? "medium" : "large"}
                  text={props.logoText}
                  textPosition={props.logoTextPosition ?? "right"}
                  href={props.logoHref}
                  onClick={props.onClickLogo}
                  dataTestId="topbar-logo"
                />
              </LogoContainer>
            )}
            {props.endAdornment || props.startAdornment
              ? (
                <Adornment>
                  {props.startAdornment && (
                    <Flex alignItems="center">{props.startAdornment}</Flex>
                  )}
                  {props.endAdornment && (
                    <EndAdornment justifyContent="flex-end" alignItems="center">
                      {props.endAdornment}
                    </EndAdornment>
                  )}
                </Adornment>
              )
              : <InnerContent>{props.children}</InnerContent>}
          </AppBarContent>
        </AppBar>
      </AppBarSticky>
    </>
  );
};
